import type { RouteRecordRaw } from 'vue-router';
import gettext from 'src/utils/gettext';
import { PermissionsCodename } from 'src/api';
import { useUser } from 'stores/user/useUser';
import { requireUserPermission } from 'src/router/guards/requireUserPermission';

const { $gettext } = gettext;

export const insuranceRoutes: RouteRecordRaw[] = [
  {
    path: 'settings',
    alias: $gettext('nastaveni'),
    beforeEnter: (to, from, next): void => {
      const userStore = useUser();
      if (
        !userStore.handleUserHasPermission(
          PermissionsCodename.LIST_MY_COMPANY_INSURANCE_SETTINGS
        )
      ) {
        return next({
          name: 'home',
        });
      }
      return next();
    },
    children: [
      {
        path: '',
        name: 'insurance-settings',
        component: () =>
          import('pages/insurance-companies/InsuranceSettingsPage.vue'),
      },
      {
        path: 'new',
        alias: $gettext('nove'),
        name: 'insurance-settings-create',
        beforeEnter: (to, from, next): void => {
          const userStore = useUser();
          if (
            !userStore.handleUserHasPermission(
              PermissionsCodename.EDIT_COMPANY_DETAILS
            )
          ) {
            return next({ name: 'insurance-settings' });
          }
          return next();
        },
        component: () =>
          import('pages/insurance-companies/InsuranceSettingsCreatePage.vue'),
      },
      {
        path: ':companyInsuranceSettingsId/edit',
        alias: ':companyInsuranceSettingsId/' + $gettext('upravit'),
        name: 'insurance-settings-update',
        props: (route) => ({
          companyInsuranceSettingsId: route.params.companyInsuranceSettingsId,
        }),
        beforeEnter: (to, from, next): void => {
          const userStore = useUser();
          if (
            !userStore.handleUserHasPermission(
              PermissionsCodename.EDIT_COMPANY_DETAILS
            )
          ) {
            return next({ name: 'insurance-settings' });
          }
          return next();
        },
        component: () =>
          import('pages/insurance-companies/InsuranceSettingsUpdatePage.vue'),
      },
    ],
  },
  {
    path: 'icpe',
    beforeEnter: requireUserPermission(PermissionsCodename.CREATE_ESICK_NOTE),
    children: [
      {
        path: '',
        name: 'esick-note-identity-list',
        component: () =>
          import('pages/insurance-companies/esick-note-identities/ESickNoteIdentityListPage.vue'),
      },
      {
        path: 'new',
        alias: $gettext('nove'),
        name: 'esick-note-identity-create',
        component: () =>
          import('pages/insurance-companies/esick-note-identities/ESickNoteIdentityCreatePage.vue'),
      },
      {
        path: ':identityId/edit',
        alias: ':identityId/' + $gettext('upravit'),
        name: 'esick-note-identity-update',
        props: (route) => ({
          identityId: route.params.identityId,
        }),
        component: () =>
          import('pages/insurance-companies/esick-note-identities/ESickNoteIdentityUpdatePage.vue'),
      },
    ],
  },
  {
    path: 'templates',
    alias: $gettext('sablony'),
    children: [
      {
        path: '',
        name: 'insurance-templates',
        component: () =>
          import('src/pages/insurance/InsuranceTemplatePage.vue'),
      },
      {
        path: 'new',
        alias: $gettext('nova'),
        name: 'insurance-new-template',
        props: (route) => ({
          next: route.query.next,
        }),
        component: () =>
          import('src/pages/insurance/FtTicketTemplateCreatePage.vue'),
      },
      {
        path: 'edit/:ftTicketTemplateId',
        alias: $gettext('upravit') + '/:ftTicketTemplateId',
        name: 'insurance-update-template',
        props: (route) => ({
          ftTicketTemplateId: route.params.ftTicketTemplateId,
        }),
        component: () =>
          import('src/pages/insurance/FtTicketTemplateUpdatePage.vue'),
      },
    ],
  },
  {
    path: 'benefits',
    alias: $gettext('davky'),
    name: 'insurance-batches',
    beforeEnter: (to, from, next): void => {
      const userStore = useUser();
      if (
        !userStore.handleUserHasPermission(PermissionsCodename.GET_K_BATCH_LIST)
      ) {
        return next({
          name: 'insurance-applicants', // this is here to allow workers access insurances tab
        });
      }
      return next();
    },
    children: [
      {
        path: '',
        name: 'insurance-batches-list',
        component: () =>
          import('pages/insurance-companies/insurance-batches/InsuranceBatchListPage.vue'),
      },
      {
        path: 'new',
        alias: $gettext('nova'),
        name: 'insurance-batch-create',
        component: () =>
          import('pages/insurance-companies/insurance-batches/InsuranceOriginalBatchCreatePage.vue'),
      },
      {
        path: ':kBatchId',
        children: [
          {
            path: 'detail',
            alias: $gettext('detail'),
            name: 'insurance-batch-detail',
            props: (route) => ({
              kBatchId: route.params.kBatchId,
            }),
            component: () =>
              import('pages/insurance-companies/insurance-batches/InsuranceBatchDetailPage.vue'),
          },
          {
            path: 'new-repair',
            alias: $gettext('nova-opravna'),
            name: 'insurance-corrective-batch-create',
            props: (route) => ({
              kBatchId: route.params.kBatchId,
              next: route.query.next,
            }),
            component: () =>
              import('pages/insurance-companies/insurance-batches/InsuranceCorrectiveBatchCreatePage.vue'),
          },
        ],
      },
    ],
  },
  {
    path: 'action-preview',
    alias: $gettext('prehled-vykonu'),
    beforeEnter: (to, from, next): void => {
      const userStore = useUser();

      const canSeeSubtab = userStore.handleUserHasPermission(
        PermissionsCodename.VIEW_INSURANCES_COMPANY_INSURANCE_ACTION_PREVIEW_SUBTAB
      );
      const canListPreview = userStore.handleUserHasPermission(
        PermissionsCodename.LIST_ACTION_PREVIEW
      );

      if (!(canSeeSubtab && canListPreview)) {
        return next({ name: 'home' });
      }

      return next();
    },
    name: 'insurance-action-preview',
    children: [
      {
        path: '',
        name: 'insurance-action-preview-list',
        component: () =>
          import('pages/insurance-companies/insurance-actions/InsuranceActionListPage.vue'),
      },
    ],
  },

  {
    path: 'performances',
    alias: $gettext('vykony'),
    children: [
      {
        path: '',
        name: 'medical-actions-list',
        component: () =>
          import('pages/office/medical-actions/MedicalActionsListPage.vue'),
      },
    ],
  },

  {
    path: 'workplace',
    alias: $gettext('pracoviste'),
    beforeEnter: (to, from, next): void => {
      const userStore = useUser();
      if (
        !userStore.handleUserHasPermission(
          PermissionsCodename.GET_COMPANY_INSURANCE_WORKSPACE_LIST
        )
      ) {
        return next({
          name: 'home',
        });
      }
      return next();
    },
    children: [
      {
        path: 'new',
        alias: $gettext('nove'),
        name: 'insurance-company-workspace-create',
        component: () =>
          import('pages/insurance-companies/insurance-company-workspaces/InsuranceCompanyWorkspaceCreatePage.vue'),
      },
      {
        path: ':companyWorkspaceId/add-insurance',
        alias: $gettext(':companyWorkspaceId/pridat-pojistovnu'),
        name: 'insurance-create',
        props: (route) => ({
          companyWorkspaceId: route.params.companyWorkspaceId,
        }),
        component: () =>
          import('pages/insurance-companies/insurance-company-workspaces/InsuranceCreatePage.vue'),
      },
      {
        path: ':companyWorkspaceId/edit-insurance/:insuranceContractId',
        alias: $gettext(
          ':companyWorkspaceId/upravit-pojistovnu/:insuranceContractId'
        ),
        name: 'insurance-update',
        props: (route) => ({
          insuranceContractId: route.params.insuranceContractId,
          companyWorkspaceId: route.params.companyWorkspaceId,
        }),
        component: () =>
          import('pages/insurance-companies/insurance-company-workspaces/InsuranceUpdatePage.vue'),
      },
      {
        path: '',
        name: 'insurance-list',
        props: (route) => ({
          newWorkspace: route.query.newWorkspace,
        }),
        component: () =>
          import('pages/insurance-companies/insurance-company-workspaces/InsuranceListPage.vue'),
      },
      {
        path: ':companyWorkspaceId/edit',
        alias: $gettext(':companyWorkspaceId/upravit'),
        name: 'insurance-company-workspace-update',
        props: (route) => ({
          companyWorkspaceId: route.params.companyWorkspaceId,
        }),
        component: () =>
          import('pages/insurance-companies/insurance-company-workspaces/InsuranceCompanyWorkspaceUpdatePage.vue'),
      },
    ],
  },
  {
    path: 'requester',
    alias: $gettext('zadatele'),
    beforeEnter: (to, from, next): void => {
      const userStore = useUser();
      if (
        !userStore.handleUserHasPermission(
          PermissionsCodename.GET_INSURANCE_WORKSPACE_LIST
        )
      ) {
        return next({
          name: 'home',
        });
      }
      return next();
    },
    children: [
      {
        path: '',
        name: 'insurance-applicants',
        component: () =>
          import('pages/insurance-companies/insurance-applicants/InsuranceApplicantListPage.vue'),
      },
      {
        path: 'new',
        alias: $gettext('novy'),
        name: 'insurance-applicant-create',
        props: (route) => ({
          next: route.query.next,
        }),
        component: () =>
          import('pages/insurance-companies/insurance-applicants/InsuranceApplicantCreatePage.vue'),
      },
      {
        path: ':insuranceApplicantId/edit',
        alias: $gettext(':insuranceApplicantId/upravit'),
        name: 'insurance-applicant-update',
        props: true,
        component: () =>
          import('pages/insurance-companies/insurance-applicants/InsuranceApplicantUpdatePage.vue'),
      },
    ],
  },
  {
    path: 'puro',
    alias: $gettext('puro'),
    beforeEnter: (to, from, next): void => {
      const userStore = useUser();
      if (!userStore.handleUserHasPermission(PermissionsCodename.VIEW_PURO)) {
        return next({
          name: 'home',
        });
      }
      return next();
    },
    children: [
      {
        path: '',
        name: 'insurance-puro',
        component: () =>
          import('pages/insurance-companies/insurance-puro/InsurancePuroPage.vue'),
      },
    ],
  },
];
